import { useState } from 'react';
import {
  Building2, BarChart3, FileText, Clock, ExternalLink, Star, RefreshCw, Pencil, Check, X,
  User, Mail, Phone, Globe, Tag, Package, MapPin, MessageSquare, ChevronDown, ChevronRight, Target,
} from 'lucide-react';
import { api, fmtDate, SEGMENT_COLORS } from './api';

const TABS = [
  { id: 'stammdaten', label: 'Stammdaten', icon: Building2 },
  { id: 'analyse',    label: 'Analyse',    icon: BarChart3 },
  { id: 'notizen',    label: 'Notizen',    icon: FileText },
  { id: 'verlauf',    label: 'Verlauf',    icon: Clock },
];

// #1047 Bug 1 + Opt 8 — Stack-Layout (Label oben, Value unten full-width) + Icon + native title-Tooltip
function EditableField({ label, value, type = 'text', icon: Icon, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await onSave(val);
      setEditing(false);
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="py-1 group">
      <div className="flex items-center gap-1.5 mb-0.5">
        {Icon && <Icon className="w-3 h-3 text-[var(--text-tertiary)] flex-shrink-0" />}
        <span className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wide flex-1">{label}</span>
        {!editing && (
          <button
            onClick={() => { setVal(value ?? ''); setEditing(true); }}
            className="p-0.5 text-[var(--text-tertiary)] hover:text-[#338edc] opacity-0 group-hover:opacity-100 transition-opacity"
            title={`${label} bearbeiten`}
          >
            <Pencil className="w-3 h-3" />
          </button>
        )}
      </div>
      {editing ? (
        <div className="flex items-center gap-1">
          <input
            type={type}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            className="flex-1 min-w-0 px-2 py-1 text-[12px] border border-[var(--border-card)] rounded bg-[var(--bg-card)] text-[var(--text-primary)]"
            autoFocus
          />
          <button onClick={save} disabled={busy} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded disabled:opacity-50" title="Speichern"><Check className="w-3.5 h-3.5" /></button>
          <button onClick={() => { setVal(value ?? ''); setEditing(false); }} className="p-1 text-[var(--text-tertiary)] hover:bg-[var(--bg-inset)] rounded" title="Abbrechen"><X className="w-3.5 h-3.5" /></button>
        </div>
      ) : (
        <div className="text-[12px] text-[var(--text-primary)] break-words" title={value ? String(value) : ''}>
          {value === null || value === undefined || value === ''
            ? <span className="text-[var(--text-tertiary)] italic">—</span>
            : value}
        </div>
      )}
    </div>
  );
}

// #1512 — Read-only Attribution-Zeile (Label oben, Value unten)
function ReadOnlyField({ label, value }) {
  return (
    <div className="py-1">
      <div className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wide mb-0.5">{label}</div>
      <div className="text-[12px] text-[var(--text-primary)] break-words" title={value ? String(value) : ''}>
        {value === null || value === undefined || value === ''
          ? <span className="text-[var(--text-tertiary)] italic">—</span>
          : value}
      </div>
    </div>
  );
}

// #1047 Opt 7 — Read-only Original-Anfrage als kollabierbare Section
function CollapsibleSection({ icon: Icon, title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-[var(--border-card)] mt-3 pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 py-1 text-left hover:opacity-80"
      >
        {Icon && <Icon className="w-3 h-3 text-[var(--text-tertiary)] flex-shrink-0" />}
        <span className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wide flex-1">{title}</span>
        {open ? <ChevronDown className="w-3 h-3 text-[var(--text-tertiary)]" /> : <ChevronRight className="w-3 h-3 text-[var(--text-tertiary)]" />}
      </button>
      {open && <div className="mt-1.5">{children}</div>}
    </div>
  );
}

function StammdatenView({ lead, onChanged }) {
  const update = async (field, value) => {
    await api(`/api/leads/${lead.id}`, { method: 'PUT', body: { [field]: value === '' ? null : value } });
    onChanged?.();
  };

  return (
    <div className="space-y-0.5">
      <EditableField icon={Building2} label="Firma" value={lead.firma} onSave={(v) => update('firma', v)} />
      <EditableField icon={User} label="Ansprechpartner" value={lead.ansprechpartner} onSave={(v) => update('ansprechpartner', v)} />
      <EditableField icon={Mail} label="E-Mail" value={lead.email} type="email" onSave={(v) => update('email', v)} />
      <EditableField icon={Phone} label="Telefon" value={lead.telefon} type="tel" onSave={(v) => update('telefon', v)} />
      <EditableField icon={Globe} label="Website" value={lead.website} onSave={(v) => update('website', v)} />
      <div className="py-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <Tag className="w-3 h-3 text-[var(--text-tertiary)] flex-shrink-0" />
          <span className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wide">Segment</span>
        </div>
        <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${SEGMENT_COLORS[lead.segment] || ''}`}>{lead.segment || '—'}</span>
      </div>
      <EditableField icon={Tag} label="Quelle" value={lead.quelle} onSave={(v) => update('quelle', v)} />
      {/* #1047 Bug 2 — versandvolumen ist VARCHAR(50), kein number-Input */}
      <EditableField icon={Package} label="Versandvolumen" value={lead.versandvolumen} onSave={(v) => update('versandvolumen', v)} />

      {/* #1512 — Attribution (read-only): nur anzeigen, wenn UTM/Click-IDs erfasst wurden */}
      {(lead.utm_source || lead.utm_medium || lead.utm_campaign || lead.utm_term || lead.utm_content || lead.gclid || lead.fbclid) && (
        <CollapsibleSection icon={Target} title="Attribution" defaultOpen={true}>
          {lead.utm_source && <ReadOnlyField label="UTM Source" value={lead.utm_source} />}
          {lead.utm_medium && <ReadOnlyField label="UTM Medium" value={lead.utm_medium} />}
          {lead.utm_campaign && <ReadOnlyField label="UTM Campaign" value={lead.utm_campaign} />}
          {lead.utm_term && <ReadOnlyField label="UTM Term" value={lead.utm_term} />}
          {lead.utm_content && <ReadOnlyField label="UTM Content" value={lead.utm_content} />}
          {lead.gclid && <ReadOnlyField label="Google Click-ID" value={lead.gclid} />}
          {lead.fbclid && <ReadOnlyField label="Meta Click-ID" value={lead.fbclid} />}
        </CollapsibleSection>
      )}

      {/* #1047 Opt 7 — Adresse als kollabierbare Section */}
      <CollapsibleSection icon={MapPin} title="Adresse" defaultOpen={!!(lead.strasse || lead.plz || lead.ort)}>
        <EditableField label="Straße" value={lead.strasse} onSave={(v) => update('strasse', v)} />
        <div className="grid grid-cols-2 gap-2">
          <EditableField label="PLZ" value={lead.plz} onSave={(v) => update('plz', v)} />
          <EditableField label="Ort" value={lead.ort} onSave={(v) => update('ort', v)} />
        </div>
        <EditableField label="Land" value={lead.land} onSave={(v) => update('land', v)} />
      </CollapsibleSection>

      {/* #1047 Opt 7 — Original-Anfrage (read-only, eingehender Lead-Text) */}
      {/* #1083 Phase 2/F — default offen, da Kern-Kontext des Leads */}
      {lead.nachricht && (
        <CollapsibleSection icon={MessageSquare} title="Original-Anfrage" defaultOpen={true}>
          <div className="text-[12px] text-[var(--text-primary)] whitespace-pre-wrap leading-relaxed bg-[var(--bg-inset)] rounded p-2 max-h-[200px] overflow-y-auto">
            {lead.nachricht}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}

function AnalyseView({ lead, onRerun }) {
  const status = lead.website_analyse_status;
  const analyse = lead.website_analyse;

  if (!lead.website) {
    return <div className="text-[12px] text-[var(--text-tertiary)] italic py-3">Keine Website hinterlegt</div>;
  }

  if (status === 'laufend' || status === 'ausstehend') {
    return (
      <div className="flex items-center gap-2 py-3">
        <div className="animate-spin h-3.5 w-3.5 border-2 border-[#338edc] border-t-transparent rounded-full" />
        <span className="text-[12px] text-[var(--text-secondary)]">Analyse läuft …</span>
      </div>
    );
  }

  if (status === 'fehler') {
    return (
      <div className="space-y-2 py-2">
        <p className="text-[12px] text-red-600">Website nicht erreichbar</p>
        <button onClick={onRerun} className="text-[11px] text-[#338edc] hover:underline flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Erneut versuchen
        </button>
      </div>
    );
  }

  if (!analyse) return <div className="text-[12px] text-[var(--text-tertiary)] italic py-3">Noch keine Analyse</div>;

  return (
    <div className="space-y-3">
      <div className="bg-[var(--bg-inset)] rounded-lg p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-[var(--text-tertiary)]">Shop-System</span>
          <span className="text-[12px] font-medium text-[var(--text-primary)]">{analyse.shop_system || 'unbekannt'}</span>
        </div>
        {analyse.marketing_tools?.length > 0 && (
          <div>
            <span className="text-[11px] text-[var(--text-tertiary)] block mb-1">Marketing</span>
            <div className="flex flex-wrap gap-1">
              {analyse.marketing_tools.map((t) => (
                <span key={t} className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-200">{t}</span>
              ))}
            </div>
          </div>
        )}
        {analyse.trustpilot_score != null && (
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-[var(--text-tertiary)]">Trustpilot</span>
            <span className="text-[12px] font-medium text-[var(--text-primary)] inline-flex items-center gap-1">
              <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
              {analyse.trustpilot_score}
              {analyse.trustpilot_count != null && <span className="text-[var(--text-tertiary)] font-normal">({analyse.trustpilot_count})</span>}
            </span>
          </div>
        )}
      </div>
      {lead.website && (
        <a href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#338edc] hover:underline inline-flex items-center gap-1">
          <ExternalLink className="w-3 h-3" /> Website öffnen
        </a>
      )}
      <button onClick={onRerun} className="text-[11px] text-[var(--text-tertiary)] hover:text-[#338edc] inline-flex items-center gap-1">
        <RefreshCw className="w-3 h-3" /> Erneut analysieren
      </button>
    </div>
  );
}

function NotizenView({ aktivitaeten = [] }) {
  const notes = aktivitaeten.filter((a) => a.typ === 'notiz');
  if (notes.length === 0) return <div className="text-[12px] text-[var(--text-tertiary)] italic py-3">Keine Notizen</div>;
  return (
    <div className="space-y-2">
      {notes.map((n) => (
        <div key={n.id} className="border border-[var(--border-card)] rounded-lg p-2.5 bg-[var(--bg-card)]">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-[11px] font-medium text-[var(--text-secondary)]">{n.erstellt_von_name || 'System'}</span>
            <span className="text-[11px] text-[var(--text-tertiary)]">{fmtDate(n.erstellt_am)}</span>
          </div>
          <div className="text-[12px] text-[var(--text-primary)] whitespace-pre-wrap">{n.inhalt}</div>
        </div>
      ))}
    </div>
  );
}

function VerlaufView({ aktivitaeten = [] }) {
  const sorted = [...aktivitaeten].sort((a, b) => new Date(b.erstellt_am) - new Date(a.erstellt_am));
  if (sorted.length === 0) return <div className="text-[12px] text-[var(--text-tertiary)] italic py-3">Kein Verlauf</div>;
  return (
    <ol className="relative border-l border-[var(--border-card)] ml-2 space-y-3 pl-3">
      {sorted.map((a) => (
        <li key={a.id} className="relative">
          <span className="absolute -left-[15px] top-1 w-2 h-2 rounded-full bg-[#338edc]" />
          <div className="text-[11px] text-[var(--text-tertiary)]">{fmtDate(a.erstellt_am)}</div>
          <div className="text-[12px] font-medium text-[var(--text-primary)]">{a.typ}</div>
          {a.inhalt && <div className="text-[11px] text-[var(--text-secondary)] truncate">{a.inhalt}</div>}
        </li>
      ))}
    </ol>
  );
}

export default function Sidebar({ lead, onChanged, onRerunAnalyse }) {
  const [tab, setTab] = useState('stammdaten');

  return (
    <aside className="w-[320px] border-l border-[var(--border-card)] bg-[var(--bg-card)] flex flex-col flex-shrink-0">
      <div className="flex border-b border-[var(--border-card)] flex-shrink-0">
        {TABS.map((tb) => {
          const Icon = tb.icon;
          const active = tab === tb.id;
          return (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium transition-colors ${
                active ? 'text-[#338edc] border-b-2 border-[#338edc]' : 'text-[var(--text-tertiary)] hover:text-[var(--text-primary)]'
              }`}
              title={tb.label}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{tb.label}</span>
            </button>
          );
        })}
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {tab === 'stammdaten' && <StammdatenView lead={lead} onChanged={onChanged} />}
        {tab === 'analyse' && <AnalyseView lead={lead} onRerun={onRerunAnalyse} />}
        {tab === 'notizen' && <NotizenView aktivitaeten={lead.aktivitaeten} />}
        {tab === 'verlauf' && <VerlaufView aktivitaeten={lead.aktivitaeten} />}
      </div>
    </aside>
  );
}
